import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  clearCurrentAlert,
  setCurrentAlert,
} from "../../store/actions/alertsSlice";
import AlertModal from "../modals/alerts/alertModal";
import Box from "@mui/material/Box";
import {
  getActionFromRegistry,
  removeActionFromRegistry,
} from "../../store/actions/alertActionsRegistry";
import OrderCancellationModal from "../modals/alerts/OrderCancellationModal";
import InternetConnectionModal from "../alerts/InternetConnectionModal";
import { sendOrderCustomerToBlacklist } from "../../api/order";
import { dispatchSnackBarMessage } from "../../utils/snackbar";
import DeliveryCenterSetupModal from "../deliveryCenter/setup/DeliveryCenterSetupModal";
import { setShowDeliveryCenterSetupModal } from "../../store/actions/deliveryCenterSlice";
import MerchantSetupModal from "../merchant/setup/MerchantSetupModal";
import { setShowMerchantSetupModal } from "../../store/actions/merchantsSlice";
import OnboardingGuidanceModal from "../onboarding/OnboardingGuidanceModal";
import { setShowOnboardingModal } from "../../store/actions/onboardingSlice";

const RECONNECTION_DISPLAY_DURATION = 300000;
const CONNECTION_CHECK_TIMEOUT = 5000;

export default function AlertManager() {
  const dispatch = useDispatch();
  const alerts = useSelector((state) => state.alerts.alerts);
  const currentAlert = useSelector((state) => state.alerts.currentAlert);

  const showDeliveryCenterSetupModal = useSelector(
    (state) => state.deliveryCenter.showDeliveryCenterSetupModal
  );

  const showMerchantSetupModal = useSelector(
    (state) => state.merchants.showMerchantSetupModal
  );

  const showOnboardingModal = useSelector(
    (state) => state.onboarding.showOnboardingModal
  );

  const [isOffline, setIsOffline] = useState(false);
  const [isReconnected, setIsReconnected] = useState(false);
  const [isCheckingInitialConnection, setIsCheckingInitialConnection] =
    useState(true);

  useEffect(() => {
    checkInitialConnection();
  }, []);

  useEffect(() => {
    const shouldSetCurrentAlert = alerts.length > 0 && !currentAlert;

    if (shouldSetCurrentAlert) {
      dispatch(setCurrentAlert(alerts[0]));
    }
  }, [alerts, currentAlert, dispatch]);

  useEffect(() => {
    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  }, []);

  const checkInitialConnection = async () => {
    const isConnected = await verifyInternetConnection();

    setIsCheckingInitialConnection(false);

    if (!isConnected) {
      setIsOffline(true);
      setIsReconnected(false);
    }
  };

  const verifyInternetConnection = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        CONNECTION_CHECK_TIMEOUT
      );

      await fetch("https://www.google.com/favicon.ico", {
        method: "HEAD",
        mode: "no-cors",
        cache: "no-store",
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleOffline = () => {
    setIsOffline(true);
    setIsReconnected(false);
  };

  const handleOnline = async () => {
    const isConnected = await verifyInternetConnection();

    if (!isConnected) {
      return;
    }

    setIsReconnected(true);

    setTimeout(() => {
      setIsOffline(false);
      setIsReconnected(false);
    }, RECONNECTION_DISPLAY_DURATION);
  };

  const handleConnectionRestored = () => {
    setIsReconnected(true);

    setTimeout(() => {
      setIsOffline(false);
      setIsReconnected(false);
    }, RECONNECTION_DISPLAY_DURATION);
  };

  const handleCloseInternetModal = () => {
    setIsOffline(false);
    setIsReconnected(false);
  };

  const handleAction = () => {
    if (!currentAlert) {
      return;
    }

    const action = getActionFromRegistry(currentAlert.actionId);

    if (!action) {
      dispatch(clearCurrentAlert());
      return;
    }

    action();
    removeActionFromRegistry(currentAlert.actionId);
    dispatch(clearCurrentAlert());
  };

  const onAddToBlacklist = async () => {
    try {
      await sendOrderCustomerToBlacklist(currentAlert.order_id);
      dispatchSnackBarMessage(
        "customer-add-blacklist-success",
        "Adicionado à Lista de Não Confiáveis",
        "success"
      );
    } catch (e) {
      console.error(e);
      dispatchSnackBarMessage(
        "customer-add-blacklist-error",
        "Erro ao colocar cliente na Lista de Não Confiáveis",
        "error"
      );
    }
  };

  const renderAlert = () => {
    if (!currentAlert) {
      return null;
    }

    switch (currentAlert.type) {
      case "normal":
        return (
          <AlertModal
            show={true}
            label={currentAlert.label}
            message={currentAlert.message}
            btnLabel={currentAlert.btnLabel}
            handleAction={handleAction}
          />
        );

      case "order-cancelled":
        return (
          <OrderCancellationModal
            show={true}
            orderId={currentAlert.order_id}
            orderIndex={currentAlert.order_index}
            cancellationReason={currentAlert.cancellation_reason}
            onAddToBlacklist={onAddToBlacklist}
            onClose={handleAction}
          />
        );

      default:
        return (
          <AlertModal
            show={true}
            label={currentAlert.label}
            message={currentAlert.message}
            btnLabel={currentAlert.btnLabel}
            handleAction={handleAction}
          />
        );
    }
  };

  const handleCloseDeliveryCenterSetupModal = useCallback(() => {
    dispatch(setShowDeliveryCenterSetupModal(false));
  }, [dispatch]);

  const handleCloseMerchantSetupModal = useCallback(() => {
    dispatch(setShowMerchantSetupModal(false));
  }, [dispatch]);

  const handleCloseOnboardingModal = useCallback(() => {
    dispatch(setShowOnboardingModal(false));
  }, [dispatch]);

  const shouldShowInternetModal = isOffline && !isCheckingInitialConnection;

  return (
    <Box>
      {renderAlert()}
      <InternetConnectionModal
        open={shouldShowInternetModal}
        onClose={handleCloseInternetModal}
        isReconnected={isReconnected}
        onConnectionRestored={handleConnectionRestored}
      />

      <DeliveryCenterSetupModal
        isOpen={showDeliveryCenterSetupModal}
        onClose={handleCloseDeliveryCenterSetupModal}
      />

      <MerchantSetupModal
        isOpen={showMerchantSetupModal}
        onClose={handleCloseMerchantSetupModal}
      />

      <OnboardingGuidanceModal
        isOpen={showOnboardingModal}
        onClose={handleCloseOnboardingModal}
      />
    </Box>
  );
}
